import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useAllProjects, useAllTasks, usePages, useProjectOf, useWorkspaceBySlug } from '@/db/hooks'
import { useScope } from '@/state/scope'
import { useUi } from '@/state/ui'
import { ALL } from '@/kinds/workspace/schema'
import { wordsLabel } from '@/kinds/page/text'
import { PageGlyph, createPage, sortPages } from '@/kinds/page/components'
import type { PageResource } from '@/kinds/page/apply'
import { Button, Empty, PageTitle, cx } from '@/components/ui'
import { ChevronDown, FileText, Plus } from 'lucide-react'
import { Icon } from '@/lib/icons'
import { AppTabs, NOTES_TABS } from '@/components/AppTabs'

/** The workspace's page tree. Pinned pages float to the top; sub-pages fold under their parent. */
export const PagesView = () => {
  const navigate = useNavigate()
  const scope = useScope((s) => s.ws)
  const home = useScope((s) => s.home)
  const pages = usePages() ?? []
  const parentOf = useProjectOf()
  const projects = useAllProjects() ?? []
  const tasks = useAllTasks() ?? []
  const wsBySlug = useWorkspaceBySlug()
  // folded nodes are remembered per device (localStorage via the ui store)
  const collapsedMap = useUi((s) => s.pagesCollapsed)
  const setCollapsedMap = useUi((s) => s.setPagesCollapsed)
  const collapsed = new Set(Object.keys(collapsedMap).filter((k) => collapsedMap[k]))
  const [menu, setMenu] = useState<'closed' | 'open' | 'parent'>('closed')
  const byId = new Map(pages.map((p) => [p.id, p]))
  const childrenOf = (id: string) => sortPages(pages.filter((p) => parentOf.get(p.id) === id))
  // roots: no parent, or a parent that is not a page (project/task)
  const roots = sortPages(pages.filter((p) => !byId.has(parentOf.get(p.id) ?? '')))
  const toggle = (id: string) => setCollapsedMap({ ...collapsedMap, [id]: !collapsedMap[id] })
  const parents = pages.filter((p) => pages.some((c) => parentOf.get(c.id) === p.id))
  const collapseAll = () => setCollapsedMap(Object.fromEntries(parents.map((p) => [p.id, true])))
  const expandAll = () => setCollapsedMap({})
  const anyFolded = parents.some((p) => collapsedMap[p.id])
  const ws = scope === ALL ? home : scope
  const open = (id: string) => void navigate({ to: '/pages/$id', params: { id } })
  const add = async () => open((await createPage(ws, undefined, roots)).id)
  // a category is just a page that groups sub-pages; the folder icon says so
  const addCategory = async () =>
    open((await createPage(ws, undefined, roots, { icon: '🗂', pinned: true })).id)
  const addUnder = async (parentId: string) => {
    setMenu('closed')
    open((await createPage(ws, parentId, childrenOf(parentId))).id)
  }
  const parentHint = (p: PageResource) => {
    const par = parentOf.get(p.id)
    if (!par) return null
    const proj = projects.find((x) => x.id === par)
    if (proj)
      return (
        <Link
          to="/projects/$id"
          params={{ id: proj.id }}
          className="hover:text-muted truncate min-w-0"
          onClick={(e) => e.stopPropagation()}
        >
          <Icon value={proj.spec.icon} size={12} fallback="▣" className="mr-1" />
          {proj.spec.title}
        </Link>
      )
    const task = tasks.find((x) => x.id === par)
    if (task)
      return (
        <Link
          to="/tasks/$id"
          params={{ id: task.id }}
          className="hover:text-muted truncate min-w-0"
          onClick={(e) => e.stopPropagation()}
        >
          ○ {task.spec.title}
        </Link>
      )
    return null
  }

  const Node = ({ page, depth }: { page: PageResource; depth: number }) => {
    const kids = childrenOf(page.id)
    const open = !collapsed.has(page.id)
    return (
      <li>
        <div
          className="flex items-center gap-1"
          style={depth ? { marginLeft: `${depth * 1}rem` } : undefined}
        >
          <button
            type="button"
            onClick={() => toggle(page.id)}
            className={cx(
              'h-8 w-6 shrink-0 text-[10px] text-faint hover:text-muted rounded',
              kids.length === 0 && 'invisible',
            )}
            aria-label={open ? 'Fold' : 'Unfold'}
            aria-expanded={open}
          >
            <span className={cx('inline-block transition-transform', open && 'rotate-90')}>▶</span>
          </button>
          <Link
            to="/pages/$id"
            params={{ id: page.id }}
            className="flex-1 flex items-center gap-2.5 rounded-lg hover:bg-panel px-2.5 py-1.5 min-h-11 min-w-0"
            data-page-id={page.id}
          >
            {scope === ALL && <Icon value={wsBySlug.get(page.labels.ws ?? '')?.spec.icon} size={14} />}
            <span className="w-6 flex items-center justify-center shrink-0 text-base">
              <PageGlyph page={page} />
            </span>
            <span className="flex-1 min-w-0">
              <span className="block truncate font-medium">{page.name}</span>
              {/* what it is and where it sits, under the title so the title keeps its width */}
              <span className="flex gap-3 min-w-0 text-xs text-faint">
                <span className="shrink-0" data-testid="page-words">
                  {wordsLabel(page.spec.text)}
                </span>
                {page.spec.pinned && <span className="shrink-0 text-accent-text">Pinned</span>}
                {parentHint(page)}
              </span>
            </span>
          </Link>
          <button
            type="button"
            onClick={() => void addUnder(page.id)}
            className="h-10 w-10 md:h-8 md:w-8 shrink-0 rounded-md text-faint/60 hover:text-fg hover:bg-raised flex items-center justify-center"
            aria-label={`New sub-page of ${page.name}`}
            title="New sub-page"
            data-testid="page-add-child"
          >
            <Plus size={14} aria-hidden />
          </button>
        </div>
        {open && kids.length > 0 && (
          <ul className="flex flex-col gap-px">
            {kids.map((k) => (
              <Node key={k.id} page={k} depth={depth + 1} />
            ))}
          </ul>
        )}
      </li>
    )
  }

  return (
    <div>
      <PageTitle
        right={
          <div className="relative flex" data-testid="page-new-menu">
            <Button
              variant="primary"
              className="rounded-r-none"
              onClick={() => void add()}
              data-testid="page-new"
            >
              + New page
            </Button>
            <Button
              variant="primary"
              className="rounded-l-none border-l border-black/20 px-2"
              onClick={() => setMenu((m) => (m === 'closed' ? 'open' : 'closed'))}
              aria-label="More ways to create"
              aria-expanded={menu !== 'closed'}
              data-testid="page-new-more"
            >
              <ChevronDown size={16} aria-hidden />
            </Button>
            {menu !== 'closed' && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setMenu('closed')} aria-hidden />
                <div className="absolute right-0 top-full mt-1 z-40 w-64 rounded-xl bg-panel border border-line shadow-[var(--shadow-pop)] p-1 fade-in">
                  {menu === 'open' ? (
                    <>
                      <button
                        type="button"
                        className="w-full text-left text-sm rounded-lg px-3 py-2 hover:bg-raised"
                        onClick={() => void add()}
                      >
                        <FileText size={14} className="inline mr-1.5 -mt-0.5 text-muted" aria-hidden />
                        Page <span className="text-faint">top level</span>
                      </button>
                      <button
                        type="button"
                        className="w-full text-left text-sm rounded-lg px-3 py-2 hover:bg-raised"
                        onClick={() => void addCategory()}
                        data-testid="page-new-category"
                      >
                        🗂 Category <span className="text-faint">a pinned page that groups others</span>
                      </button>
                      <button
                        type="button"
                        className="w-full text-left text-sm rounded-lg px-3 py-2 hover:bg-raised flex justify-between"
                        onClick={() => setMenu('parent')}
                        data-testid="page-new-sub"
                      >
                        <span>↳ Sub-page of…</span>
                        <span className="text-faint">›</span>
                      </button>
                    </>
                  ) : (
                    <div className="max-h-72 overflow-y-auto">
                      <div className="px-3 py-1.5 text-[13px] font-semibold text-faint">
                        Under which page?
                      </div>
                      {sortPages(pages).map((p) => (
                        <button
                          key={p.id}
                          type="button"
                          className="w-full text-left text-sm rounded-lg px-3 py-2 hover:bg-raised truncate"
                          onClick={() => void addUnder(p.id)}
                        >
                          <PageGlyph page={p} size={14} className="inline-block mr-1.5 align-[-2px]" />
                          {p.name}
                        </button>
                      ))}
                      {pages.length === 0 && <p className="px-3 py-2 text-sm text-faint">No pages yet.</p>}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        }
      >
        Notes
      </PageTitle>
      <AppTabs tabs={NOTES_TABS} label="Notes" />
      {parents.length > 0 && (
        <div className="flex justify-end mb-1.5 -mt-2">
          <button
            type="button"
            onClick={anyFolded ? expandAll : collapseAll}
            className="text-xs text-muted hover:text-fg"
            data-testid="pages-fold-all"
          >
            {anyFolded ? 'Expand all' : 'Collapse all'}
          </button>
        </div>
      )}
      {roots.length === 0 ? (
        <Empty icon={<FileText size={30} strokeWidth={1.5} aria-hidden />}>
          No pages yet. Start one with New page, or add a page from any project or task.
        </Empty>
      ) : (
        <ul className="card flex flex-col gap-px p-1" data-testid="pages-tree">
          {roots.map((p) => (
            <Node key={p.id} page={p} depth={0} />
          ))}
        </ul>
      )}
    </div>
  )
}
